import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import { FiArrowUpRight } from 'react-icons/fi';

import { AppWrap, MotionWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import './Testimonial.scss';

const Testimonial = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState('');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const query = '*[_type == "testimonials"] | order(_createdAt desc)';
    const brandsQuery = '*[_type == "brands"] | order(name asc)';

    Promise.all([client.fetch(query), client.fetch(brandsQuery)])
      .then(([certificateData, brandData]) => {
        setTestimonials(certificateData);
        setBrands(brandData);
      })
      .catch(() => setError('Credentials could not be loaded right now.'));
  }, []);

  const selectPrevious = () => {
    setCurrentIndex((index) => (index === 0 ? testimonials.length - 1 : index - 1));
  };

  const selectNext = () => {
    setCurrentIndex((index) => (index === testimonials.length - 1 ? 0 : index + 1));
  };

  const activeCertificate = testimonials[currentIndex];

  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">06 / Credentials</p>
          <h2 className="head-text">Learning is part of <span>the practice.</span></h2>
        </div>
        <p className="section-heading__copy">
          Verified coursework spanning frontend engineering, version control,
          software process, and the web platform.
        </p>
      </div>

      {error && <p className="status-message status-message--error" role="status">{error}</p>}

      {activeCertificate && (
        <div className="certificate-layout">
          <div className="certificate-stage">
            <AnimatePresence mode="wait">
              <motion.a
                key={activeCertificate._id}
                href={activeCertificate.url}
                target="_blank"
                rel="noreferrer"
                className="certificate-image"
                initial={reduceMotion ? false : { opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, x: -20 }}
                transition={{ duration: 0.35 }}
                aria-label={`Verify ${activeCertificate.name} certificate`}
              >
                {activeCertificate.imgurl && (
                  <img
                    src={urlFor(activeCertificate.imgurl).width(1400).height(950).fit('max').auto('format').url()}
                    alt={`${activeCertificate.name} certificate`}
                    loading="lazy"
                  />
                )}
                <span>Verify credential <FiArrowUpRight /></span>
              </motion.a>
            </AnimatePresence>
          </div>

          <div className="certificate-details">
            <p className="certificate-count">
              {String(currentIndex + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
            </p>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCertificate._id}
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: 0.28 }}
              >
                <p className="certificate-provider">{activeCertificate.company}</p>
                <h3>{activeCertificate.name}</h3>
              </motion.div>
            </AnimatePresence>
            <div className="certificate-controls">
              <button type="button" onClick={selectPrevious} aria-label="Previous certificate">
                <HiArrowLeft />
              </button>
              <button type="button" onClick={selectNext} aria-label="Next certificate">
                <HiArrowRight />
              </button>
            </div>
          </div>

          <div className="certificate-index" aria-label="Choose a certificate">
            {testimonials.map((certificate, index) => (
              <button
                type="button"
                className={index === currentIndex ? 'is-active' : ''}
                onClick={() => setCurrentIndex(index)}
                key={certificate._id}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                {certificate.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {brands.length > 0 && (
        <div className="app__testimonial-brands">
          <p className="skills-label">Worked with</p>
          <div>
            {brands.map((brand) => (
              <img
                key={brand._id}
                src={urlFor(brand.imgUrl).width(260).height(120).fit('max').auto('format').url()}
                alt={brand.name}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AppWrap(
  MotionWrap(Testimonial, 'app__testimonial'),
  'testimonial',
  'app__whitebg',
);
